// Számla-lista (billing, BELSŐ nézet) — a KÖZÖS DataTable-re épül (a clients.index / tickets / accounts a
// referencia-minta): szerver-oldali (BFF) kereső (300 ms debounce) + státusz/kibocsátó-szűrő + darabszám-meta a
// toolbarban, szerver-rendezés (invoiceNumber / issuerName / grossTotal / issueDate), végtelen görgetés (100/lap).
// A /invoices BFF-végpontról fogyaszt (busz-sorok + kibocsátó + számlázott fiók(ok) feloldva; tenant-scope
// grant-fal RLS Odin-oldalon). Page-gate: `invoices.view`.
//
// Legacy-parity (régi CRM belső „Számlák" lista): a régi 4 fül (Piszkozat/Kiállítva/Fizetve/Sztornó) helyett a
// KÖZÖS státusz-szűrő dropdown fedi le mind a 6 státuszt (a fülek legacy UI-affordancia; a dropdown a hű
// adat-parity). A legacy összevont cellái 1:1 a következő oszlopokra képződnek:
//   1. Számla        — számlaszám (itemTitle, rendezhető) + típus-badge + státusz-badge   (legacy „Számlaszám")
//   2. Számlázott fél — a számlázott fiók(ok) neve (BFF-merge, foundation accounts)         (legacy „Számlázási adatok"*)
//   3. Kibocsátó      — a kibocsátó (issuerName snapshot; rendezhető)                        (legacy „számlatömb"/prefix)
//   4. Összeg         — nettó (fő) + ÁFA + bruttó, pénznemmel, jobbra igazítva (rendezhető: bruttó)  (legacy „Összeg")
//   5. Dátumok        — kiállítás + fizetési határidő (lejártkor a határidő piros; rendezhető: kiállítás)  (legacy „Dátumok")
// (*) A legacy „Számlázási adatok" a vevő billing_name/cím/adószám snapshotját mutatja — ezek NINCSENEK a
// contractban → helyette a számlázott fiók(ok) nevét oldjuk fel (ami elérhető). NEM renderelhető a contractból,
// ezért KIHAGYVA: billing_agreement-badge, related/storno-számlaszám, fizetés dátuma (payment_date), sztornónál a
// negatív nettó. Az „időszak" (issue_date-tartomány) szűrőhöz nincs date-range atom a design-rendszerben → kihagyva.
//
// NAVIGÁCIÓ + SOR-AKCIÓK: a sor-kattintás (bármely státusz) → read-only számla-detail (`/invoices/$id`); a
// sor-akciók: MINDEN soron „Megtekintés" (szem), draft soron ezen felül ceruza (szerkesztés, invoices.manage)
// + kuka (törlés, invoices.delete). A kiállított számla immutábilis (issue/storno/PDF a Számlázz.hu-fázis).
import { type InvoiceRowSchema } from '@atlas/contracts';
import {
  ArrowPathIcon,
  ArrowUturnLeftIcon,
  DocumentPlusIcon,
  ExclamationTriangleIcon,
  PaperAirplaneIcon,
  PlusIcon,
  ReceiptPercentIcon,
} from '@heroicons/react/24/outline';
import { useInfiniteQuery, type InfiniteData } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { InvoiceStatusBadge } from '@/components/invoices/invoice-status-badge';
import { INVOICE_DOC_KINDS } from '@atlas/shared';
import { InvoiceIssuerBadge } from '@/components/invoices/invoice-issuer-badge';
import { InvoiceTypeBadge } from '@/components/invoices/invoice-type-badge';
import { AppShell } from '@/components/shared/app-shell';
import { ConfirmDialog } from '@/components/shared/confirm-dialog';
import { DataTable, type DataTableColumn } from '@/components/shared/data-table';
import { EmptyState } from '@/components/shared/empty-state';
import { ExpandableCell } from '@/components/shared/expandable-cell';
import { LabelChip } from '@/components/shared/label-chip';
import { ListErrorState } from '@/components/shared/list-error-state';
import { ListNewButton } from '@/components/shared/list-new-button';
import { ListToolbar } from '@/components/shared/list-toolbar';
import { MonthStepper } from '@/components/shared/month-stepper';
import { Button } from '@/components/ui/button';
import { Divider } from '@/components/ui/divider';
import { Tabs, TabsCount, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Text } from '@/components/ui/text';
import { invoiceKeys } from '@/features/invoices/api';
import { GenerationSkipsTable } from '@/features/invoices/components/generation-skips-table';
import {
  InvoiceActionDialogs,
  useInvoiceActions,
} from '@/features/invoices/components/invoice-action-dialogs';
import { InvoiceGenerateDialog } from '@/features/invoices/components/invoice-generate-dialog';
import { InvoiceRowActions } from '@/features/invoices/components/invoice-row-actions';
import { useInvoiceLifecycle } from '@/features/invoices/lifecycle';
import { currentMonth, monthRange } from '@/lib/month';
import { useListFilters } from '@/hooks/use-list-filters';
import { api } from '@/lib/api';
import { requireAuth } from '@/lib/auth';
import { formatDay } from '@/lib/format-datetime';
import { formatMoney } from '@/lib/format-money';
import { useEntityMutation } from '@/lib/use-entity-mutation';
import { usePermissions } from '@/lib/use-permissions';
import { isForbiddenStatus } from '@/lib/error-status';

// A SZŰRŐK search-paramok (a state-konvenció: a szűrt nézet legyen linkelhető és F5-biztos) — a route-nak
// ismernie kell a kulcsokat, különben a router kiszűri őket. A default-értékű kulcs nem kerül az URL-be.
const invoicesSearchSchema = z.object({
  // FÜL: `invoices` (alap, nincs az URL-ben) | `skips` — a „Generálási kihagyások" napló.
  tab: z.string().optional(),
  // HÓNAP — a lap KÖZÖS dimenziója (mindkét fül erre néz), prefix nélkül, mint a Pénzügy-lapon.
  month: z.string().optional(),
  q: z.string().optional(),
  status: z.string().optional(),
  type: z.string().optional(),
  dueFrom: z.string().optional(),
  dueTo: z.string().optional(),
  sort: z.string().optional(),
  order: z.string().optional(),
  // A „Generálási kihagyások" fül SAJÁT szűrői (`skip`-előtaggal, hogy ne ütközzenek a fenti kulcsokkal).
  // A HÓNAP nincs köztük: az a lap közös dimenziója (`month`) — a két fül ugyanazt a hónapot mutatja.
  skipReason: z.string().optional(),
  skipClientId: z.string().optional(),
  skipState: z.string().optional(),
  skipQ: z.string().optional(),
});

export const Route = createFileRoute('/invoices/')({
  validateSearch: (search) => invoicesSearchSchema.parse(search),
  beforeLoad: () => requireAuth(),
  component: InvoicesPage,
});

type InvoiceRow = z.infer<typeof InvoiceRowSchema>;
type ListPage = Awaited<ReturnType<typeof api.invoices.query>>;

const PAGE_SIZE = 100;
const ALL = '__all__';

// A legacy enum-kulcsok (adat-parity a régi CRM-mel) — a címkék az i18n-ből.
/**
 * A STÁTUSZ-szűrő értékei: a legacy négy FÜLE (Piszkozat · Kiállított · Kiegyenlített · Érvénytelenített)
 * csoport-értékként, majd a konkrét státuszok. A legacy „Kiállított" fülön a LEJÁRT sorok is ott voltak, az
 * „Érvénytelenített" fülön pedig a sztornózott számla és az érvénytelenített díjbekérő EGYÜTT — a
 * `group:*` értékek ezt a nézetet adják vissza a szűrő-alapú felületen (a BFF oldja fel őket).
 */
const STATUS_OPTIONS = [
  'draft',
  'group:issued',
  'paid',
  'group:void',
  'issued',
  'overdue',
  'cancelled',
  'storno',
];
// A legacy „számlatömb" (InvoicePrefixEnum) — a soron lévő issuerKey.
const ISSUER_OPTIONS = ['MOR', 'MDIG', 'none'];

/**
 * „irsz város, utca" EGY sorban — a számlázási profilok és a fiókok lista cím-sorának alakja (rendszer-
 * konvenció). Üres részek kimaradnak; ha semmi nincs, üres string (a hívó dönt az em-dash-ről).
 */
function addressLine(zip: string | null, city: string | null, address: string | null): string {
  const cityLine = [zip, city].filter((x) => x !== null && x.trim() !== '').join(' ');
  return [cityLine, address].filter((x) => x !== null && x.trim() !== '').join(', ');
}

/**
 * Adószám-sor: `LabelChip` (országkód, illetve „EU") + a szám — a számlázási profilok adószám-oszlopának
 * mintája, de a lista 2./3. oszlopában HARMADIK sorként, ezért `tone="tertiary"` (halványabb) ÉS egy fokkal
 * kisebb betű (`text-sm`, 12 px), mint a fölötte álló cím-sor (13 px) — a sor MAGASSÁGA viszont a cím-sorral
 * azonos (`leading-[1.125rem]`, a `text-md` sor-magassága), így a három sor ritmusa nem törik meg; a
 * `tabular-nums` marad (user-döntés). Mindkét szám opcionális; a hívó előbb ellenőrzi, hogy van-e
 * legalább egy.
 */
function TaxLine({
  countryCode,
  taxNumber,
  euTaxNumber,
}: {
  countryCode: string | null;
  taxNumber: string | null;
  euTaxNumber: string | null;
}): JSX.Element {
  return (
    <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-0.5 text-sm leading-[1.125rem]">
      {taxNumber && (
        <span className="flex min-w-0 items-center gap-1">
          <LabelChip label={countryCode ?? 'HU'} />
          <Text
            tone="tertiary"
            as="span"
            className="truncate text-sm tabular-nums leading-[1.125rem]"
          >
            {taxNumber}
          </Text>
        </span>
      )}
      {euTaxNumber && (
        <span className="flex min-w-0 items-center gap-1">
          <LabelChip label="EU" />
          <Text
            tone="tertiary"
            as="span"
            className="truncate text-sm tabular-nums leading-[1.125rem]"
          >
            {euTaxNumber}
          </Text>
        </span>
      )}
    </div>
  );
}

export function InvoicesPage(): JSX.Element {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { can, isLoading: permsLoading } = usePermissions();
  const canView = can('invoices.view');
  // Jog-vezérelt akciók: invoices.manage → „Új számla" + ceruza (csak draft soron); invoices.delete → kuka
  // (csak draft soron, ConfirmDialog). A tényleges izolációt/írás-jogot az Odin RLS/scope + a BFF-guard adja.
  const canManage = can('invoices.manage');
  const canDelete = can('invoices.delete');

  // Számla-kiállítási életciklus (Számlázz.hu): kiállítás/sztornó/érvénytelenítés dialógusok + PDF + szinkron.
  const actions = useInvoiceActions(t);

  const [deleteTarget, setDeleteTarget] = useState<InvoiceRow | null>(null);
  // Számlagenerálás-dialógus (admin, invoices.manage) — a KÉSZ POST /invoices/generate trigger.
  const [generateOpen, setGenerateOpen] = useState(false);
  /**
   * SOR-KIJELÖLÉS a tömeges kiállításhoz (a legacy „bulk issue"). CSAK draft sor jelölhető ki: a `issue`
   * művelet nem-draft soron 409-et adna, tehát a nem-jelölhető sor hamis affordancia lenne.
   */
  const [selectedIds, setSelectedIds] = useState<ReadonlySet<string>>(new Set());
  const [bulkConfirm, setBulkConfirm] = useState(false);
  // A tömeges művelet a lista-szintű lifecycle-mutációkat használja (a sor-akciók ugyanezt a hookot kapják).
  const lifecycle = useInvoiceLifecycle(t, () => setSelectedIds(new Set()));
  // A SZŰRŐK az URL-ben (linkelhető + F5-biztos nézet); a defaultok egyben a kulcs-készlet.
  const { filters: urlFilters, setFilters } = useListFilters({
    // FÜL: `invoices` (alap) | `skips`. A `GenerationSkipsTable` a SAJÁT (`skip`-előtagú) kulcsait a
    // ugyanezen a `useListFilters`-hívás-készleten kívül, a saját hookjában kezeli — a router csak akkor
    // engedi át őket, ha a route `validateSearch`-e ismeri (lásd `invoicesSearchSchema`).
    tab: 'invoices',
    // A HÓNAP a lap közös dimenziója: a lista a hónapban KIÁLLÍTOTT számlákat mutatja, a másik fül pedig
    // ugyanannak a hónapnak a generálási kihagyásait. Alap: a jelen hónap (a Pénzügy- és a használati
    // adatok lap ugyanígy nyit).
    month: currentMonth(),
    q: '',
    status: ALL,
    type: ALL,
    dueFrom: '',
    dueTo: '',
    sort: 'issueDate',
    order: 'desc',
  });
  const tab = urlFilters.tab === 'skips' ? 'skips' : 'invoices';
  const q = urlFilters.q;
  const status = urlFilters.status;
  const type = urlFilters.type;
  const month = urlFilters.month;
  const dueFrom = urlFilters.dueFrom;
  const dueTo = urlFilters.dueTo;
  const sort = urlFilters.sort;
  const order = urlFilters.order as 'asc' | 'desc';
  // A beviteli mező AZONNALI (lokális) állapota — az URL-be a debounce-olt érték kerül.
  const [searchInput, setSearchInput] = useState(q);
  const [issuer, setIssuer] = useState<string>(ALL);
  // Szerver-rendezés: a `sort` a kulcs, az `order` az irány. Alap: issueDate DESC (a legacy default — legfrissebb elöl).

  // Kereső-debounce (300 ms).
  useEffect(() => {
    const trimmed = searchInput.trim();
    if (trimmed === q) return;
    const timer = setTimeout(() => setFilters({ q: trimmed }), 300);
    return () => clearTimeout(timer);
  }, [searchInput, q, setFilters]);

  // Page-gate: invoices.view nélkül meg sem kérdezzük a szervert (a BFF is 403-at adna) → azonnali jog-hiba.
  const gated = !permsLoading && !canView;

  // A FÜL darabszám-plakettje: a NYITOTT kihagyások száma a KIVÁLASZTOTT hónapra — ugyanarra, amit a fül
  // maga is mutatna (a hónap a lap közös dimenziója). Jog-kapu: invoices.view.
  const skipsCountQ = api.invoiceGenerationSkips.useQuery(
    ['invoices', 'generation-skips', 'tab-count', month],
    { query: { month, limit: 1 } },
    {
      enabled: canView,
      queryKey: ['invoices', 'generation-skips', 'tab-count', month],
    },
  );
  const skipsCount = skipsCountQ.data?.status === 200 ? skipsCountQ.data.body.total : null;

  // A HÓNAP nap-pontos tartománnyá fordul a lista-végponthoz (`issuedFrom`/`issuedTo`): a végpont
  // nap-intervallumot szűr, a lap viszont hónap-dimenzióban gondolkodik (`monthRange` — egy helyen).
  const { from: issuedFrom, to: issuedTo } = monthRange(month);
  const filters = { q, status, type, issuer, issuedFrom, issuedTo, dueFrom, dueTo, sort, order };
  const listKey = invoiceKeys.list(filters);

  const infinite = useInfiniteQuery({
    queryKey: listKey,
    enabled: !gated,
    queryFn: ({ pageParam }) =>
      api.invoices.query({
        query: {
          q: q || undefined,
          status: status === ALL ? undefined : status,
          type: type === ALL ? undefined : type,
          issuer: issuer === ALL ? undefined : issuer,
          issuedFrom: issuedFrom || undefined,
          issuedTo: issuedTo || undefined,
          dueFrom: dueFrom || undefined,
          dueTo: dueTo || undefined,
          sort,
          order,
          limit: PAGE_SIZE,
          offset: pageParam,
        },
      }),
    initialPageParam: 0,
    getNextPageParam: (lastPage) =>
      lastPage.status === 200 ? (lastPage.body.nextOffset ?? undefined) : undefined,
  });

  const pages = useMemo(() => infinite.data?.pages ?? [], [infinite.data]);
  const rows = useMemo(
    () => pages.flatMap((p) => (p.status === 200 ? p.body.invoices : [])),
    [pages],
  );
  const okPage = pages.find((p) => p.status === 200);
  const total = okPage?.status === 200 ? okPage.body.total : 0;
  // A BFF jelzi, ha a lista a védő-plafon miatt CSONKA (a darabszám és minden aggregátum is az) —
  // ezt kiírjuk a meta-sorba: egy csendben elvágott lista hibás döntést alapoz meg.
  const truncated = okPage?.status === 200 ? okPage.body.truncated : false;
  const hasNoSuccess = pages.length > 0 && !pages.some((p) => p.status === 200);
  const isError = gated || infinite.isError || hasNoSuccess;
  const isForbidden = gated || pages.some((p) => isForbiddenStatus(p.status));
  /**
   * A kijelölésből a TÉNYLEGESEN kiállítható sorok: csak DRAFT. A kijelölés a betöltött lapokra épül, ezért a
   * szűrő-/lap-váltás után is a `rows`-ból származtatjuk (egy már nem látszó sor nem kerül a műveletbe).
   */
  const selectedDraftIds = rows
    .filter((r) => selectedIds.has(r.id) && r.status === 'draft')
    .map((r) => r.id);

  const hasFilters = q !== '' || status !== ALL || issuer !== ALL;

  // Rendezés: azonos oszlopra kattintás → irányváltás; új oszlopra → az adott kulcs ASC.
  // Oszlop-fejléc kattintás: ugyanaz az oszlop → irány-váltás; MÁS oszlop → az új oszlop, `asc`-ből.
  const toggleSort = useCallback(
    (col: string) => {
      setFilters(
        col === sort ? { order: order === 'asc' ? 'desc' : 'asc' } : { sort: col, order: 'asc' },
      );
    },
    [sort, order, setFilters],
  );
  const { fetchNextPage } = infinite;
  const loadMore = useCallback(() => void fetchNextPage(), [fetchNextPage]);

  // Törlés (CSAK draft): optimista lista-sor eltávolítás + rollback; a soft-delete a BFF `DELETE /invoices/:id`
  // (invoices.delete + If-Match a sor verziójából). Nem-draft sor → nincs is kuka (a rowActions rejti).
  const removeMutation = useEntityMutation<{ id: string; version: number }, InfiniteData<ListPage>>(
    {
      mutationFn: ({ id, version }) =>
        api.invoiceDelete.mutation({ params: { id }, headers: { 'if-match': String(version) } }),
      invalidateKeys: [invoiceKeys.lists()],
      optimistic: {
        queryKey: listKey,
        update: (old) => {
          const id = deleteTarget?.id;
          if (!old || !id) return old;
          return {
            ...old,
            pages: old.pages.map((p) =>
              p.status === 200
                ? {
                    ...p,
                    body: {
                      ...p.body,
                      invoices: p.body.invoices.filter((inv) => inv.id !== id),
                      total: Math.max(0, p.body.total - 1),
                    },
                  }
                : p,
            ),
          };
        },
      },
      messages: {
        success: t('toast:invoices.deleted'),
        conflict: t('toast:invoices.conflict'),
        forbidden: t('toast:invoices.forbidden'),
        validation: t('toast:invoices.validation'),
        error: t('toast:invoices.error'),
      },
    },
  );

  function confirmDelete(): void {
    if (!deleteTarget) return;
    removeMutation.mutate(
      { id: deleteTarget.id, version: deleteTarget.version },
      { onSettled: () => setDeleteTarget(null) },
    );
  }

  // Halvány em-dash az üres tartalmi cellákhoz.
  const emDash = <span className="text-muted-foreground">—</span>;
  const locale = i18n.language;

  const columns: DataTableColumn<InvoiceRow>[] = [
    {
      // 1. Számla — számlaszám (rendezhető, itemTitle) + típus-badge + státusz-badge (a legacy „Számlaszám" oszlop).
      id: 'invoiceNumber',
      header: t('invoices.columns.invoice'),
      sortable: true,
      cardLabel: t('invoices.columns.invoice'),
      // A hasáb a BIZONYLATSZÁMOT teljes hosszában elbírja: az ideiglenes szám (`TMP-INV-260826-001`) 18
      // karakter, és az 1.05fr-en csonkolt („TMP-INV-26…" — mérve 1600px-en). A snapshot-oszlopok cím-sora
      // ettől még kiférjen, ezért a számlázott fél hasábja kicsit szűkült.
      width: 'minmax(0,1.7fr)',
      multiline: true,
      cell: (inv) => {
        // A PISZKOZAT is BESZÉDES számot mutat (`TMP-INV-260826-001` — a legacy formátuma): a lista addig
        // MINDEN piszkozatra ugyanazt írta („Piszkozat"), tehát a sorok azonosíthatatlanok voltak
        // (user-lelet). A placeholder MOSTANTÓL csak arra a néhány sorra jut, aminek tényleg nincs
        // száma: a régi, technikai `DRAFT-{uuid}` bizonylatokra. A végleges számot a kiállítás adja
        // (Számlázz.hu, Függő increment) — a `TMP` csak a piszkozat-élet ideiglenes azonosítója.
        const isDraftNumber =
          inv.invoiceNumber === null ||
          inv.invoiceNumber === '' ||
          inv.invoiceNumber.startsWith('DRAFT-');
        return (
          <div className="min-w-0 space-y-1.5">
            <Text variant="itemTitle" as="div" className="truncate">
              {isDraftNumber ? (
                <span className="text-muted-foreground italic">
                  {t('invoices.draftNumberPlaceholder')}
                </span>
              ) : (
                (inv.invoiceNumber ?? emDash)
              )}
            </Text>
            {/* KAPCSOLÓDÓ számla (autokassza): a kifizetett díjbekérőhöz a Számlázz.hu állítja ki a számlát, és
                a napi státusz-szinkron írja ide a számát. A legacy lista is kiírta a bizonylatszám ALATT, egy
                visszafordító nyíllal — enélkül a felületen nem látszik, hogy a díjbekérőből lett-e számla. */}
            {inv.relatedInvoiceNumber && (
              <Text tone="tertiary" as="div" className="flex items-center gap-1 truncate">
                <ArrowUturnLeftIcon className="size-3 shrink-0" aria-hidden />
                <span className="truncate">{inv.relatedInvoiceNumber}</span>
              </Text>
            )}
            <div className="flex flex-wrap items-center gap-1">
              {inv.type && (
                <InvoiceTypeBadge
                  type={inv.type}
                  deliveryType={inv.deliveryType}
                  status={inv.status}
                  t={t}
                />
              )}
              {/* a kibocsátó (számlatömb) a bizonylattípus UTÁN — a „Számlánként" nézettel azonos jelvény */}
              <InvoiceIssuerBadge issuerKey={inv.issuerKey} t={t} />
            </div>
          </div>
        );
      },
    },
    {
      // 2. Számlázott fél — HÁROM sor a rendszer-konvenció szerint (mint a számlázási profilok listája):
      //    (a) a fiók-név(ek) — az oszlop alap-betűje (`text-muted-foreground`),
      //    (b) „irsz város, utca" egy sorban, `tone="tertiary"` (kisebb/halványabb),
      //    (c) az adószám(ok) `LabelChip`-pel (országkód / EU), szintén tertiary.
      // Az adat a számlán TÁROLT vevő-snapshot (jogi bizonylat: azt mutatjuk, ami rajta van). Ha a fiók-név
      // nem oldódott fel, a snapshot `billingName` a fej-sor — enélkül a cella címke nélkül maradt volna.
      id: 'billingParty',
      card: 'subtitle',
      header: t('invoices.columns.billingParty'),
      cardLabel: t('invoices.columns.billingParty'),
      width: 'minmax(0,1.95fr)',
      className: 'text-muted-foreground',
      multiline: true,
      cell: (inv) => {
        const names = inv.accountNames.length > 0 ? inv.accountNames : [];
        const head =
          names.length > 0 ? (
            <ExpandableCell
              items={names}
              getKey={(n, i) => `${n}-${i}`}
              visible={2}
              threshold={3}
              moreLabel={(n) => t('invoices.more', { count: n })}
              triggerAriaLabel={t('invoices.moreItems')}
              renderItem={(n) => <span className="block truncate">{n}</span>}
            />
          ) : inv.billingName ? (
            <div className="truncate">{inv.billingName}</div>
          ) : null;
        const address = addressLine(inv.billingZipCode, inv.billingCity, inv.billingAddress);
        const hasTax = !!inv.billingTaxNumber || !!inv.billingEuTaxNumber;
        if (head === null && !address && !hasTax) return emDash;
        return (
          <div className="min-w-0 space-y-0.5">
            {head}
            {address && (
              <Text tone="tertiary" as="div" className="truncate">
                {address}
              </Text>
            )}
            {hasTax && (
              <TaxLine
                countryCode={inv.billingCountryCode}
                taxNumber={inv.billingTaxNumber}
                euTaxNumber={inv.billingEuTaxNumber}
              />
            )}
          </div>
        );
      },
    },
    {
      // 3. Kibocsátó — ugyanaz a HÁROM soros minta, mint a számlázott félnél (név · cím · adószám-chipek).
      //    A név a snapshot `issuerName` (fallback a kulcs-címke). Rendezés: issuerName.
      //    Mobil-kártyán REJTVE — másodlagos mező (a kártyán a szám + fél + összeg + dátum + státusz a lényeg).
      id: 'issuerName',
      header: t('invoices.columns.issuer'),
      sortable: true,
      cardLabel: t('invoices.columns.issuer'),
      width: 'minmax(0,1.55fr)',
      className: 'text-muted-foreground',
      card: 'hidden',
      cell: (inv) => {
        const address = addressLine(inv.issuerZipCode, inv.issuerCity, inv.issuerAddress);
        const hasTax = !!inv.issuerTaxNumber || !!inv.issuerEuTaxNumber;
        // A BFF a hiányzó név-snapshotot a NYERS kulccsal pótolja (`issuerName ?? issuerKey`) — az a
        // felületen „none"-ként jelent meg (mérve: 489 migrált fej név nélkül). A kulcsot a számlatömb
        // lokalizált címkéjére fordítjuk („—" / „MOR" / „MDIG"), mint a szűrő és a számla-űrlap.
        const issuerLabel =
          inv.issuerName && inv.issuerName !== inv.issuerKey
            ? inv.issuerName
            : inv.issuerKey
              ? t(`invoices.issuerLabel.${inv.issuerKey}`, { defaultValue: inv.issuerKey })
              : null;
        if (!issuerLabel && !address && !hasTax) return emDash;
        return (
          <div className="min-w-0 space-y-0.5">
            {issuerLabel && <div className="truncate">{issuerLabel}</div>}
            {address && (
              <Text tone="tertiary" as="div" className="truncate">
                {address}
              </Text>
            )}
            {hasTax && (
              <TaxLine
                countryCode={inv.issuerCountryCode}
                taxNumber={inv.issuerTaxNumber}
                euTaxNumber={inv.issuerEuTaxNumber}
              />
            )}
          </div>
        );
      },
    },
    {
      // 4. Összeg — az ESZKÖZÖK lista ár-cellájának mintája (user-döntés): `címke ─── érték`
      //    párok, a bruttó előtt hajszálvonallal. Így a három szám EGY oszlopba igazodik és a bruttó
      //    optikailag elválik — a korábbi „szám, alatta »ÁFA: …« / »Bruttó: …«" jobbra-tolt blokk nehezen
      //    volt letapogatható. Rendezés: bruttó. `tabular-nums`: a számjegyek fix szélességűek.
      // A blokk (fejléccel együtt) BALRA igazított, mint minden oszlop (user-döntés) — a
      //    számok a blokk JOBB élén futnak egy vonalban, a blokk maga a cella bal élén kezdődik.
      id: 'grossTotal',
      card: 'trailing',
      header: t('invoices.columns.amount'),
      sortable: true,
      cardLabel: t('invoices.columns.amount'),
      width: 'minmax(0,1.6fr)',
      className: 'text-muted-foreground',
      multiline: true,
      // Export: a három érték `címke: érték` párokként (a cella olvasata, nem a nyers szám-hármas).
      exportValue: (inv) =>
        [
          `${t('invoices.net')}: ${formatMoney(inv.netTotal, inv.currency, locale)}`,
          `${t('invoices.vat')}: ${formatMoney(inv.vatTotal, inv.currency, locale)}`,
          `${t('invoices.gross')}: ${formatMoney(inv.grossTotal, inv.currency, locale)}`,
        ].join('\n'),
      cell: (inv) => (
        <div className="flex min-w-0 max-w-[13rem] flex-col gap-0.5 text-xs">
          <div className="flex justify-between gap-3">
            <span>{t('invoices.net')}</span>
            <span className="text-foreground whitespace-nowrap font-medium tabular-nums">
              {formatMoney(inv.netTotal, inv.currency, locale)}
            </span>
          </div>
          <div className="flex justify-between gap-3">
            <span>{t('invoices.vat')}</span>
            <span className="whitespace-nowrap tabular-nums">
              {formatMoney(inv.vatTotal, inv.currency, locale)}
            </span>
          </div>
          <Divider tone="hairline" inset={false} spacing="none" className="my-0.5" />
          <div className="flex justify-between gap-3">
            <span>{t('invoices.gross')}</span>
            <span className="whitespace-nowrap tabular-nums">
              {formatMoney(inv.grossTotal, inv.currency, locale)}
            </span>
          </div>
        </div>
      ),
    },
    {
      // 5. Dátumok — kiállítás (fő, rendezhető) + fizetési határidő (lejártkor piros — a legacy 1:1).
      id: 'issueDate',
      header: t('invoices.columns.dates'),
      sortable: true,
      cardLabel: t('invoices.columns.dates'),
      // A CÍMKÉZETT dátumok („Kiállítva: …" / „Fizetési határidő: …") 1.25fr-en CSONKOLTAK (mérve).
      width: 'minmax(0,1.5fr)',
      // A 2. oszloptól halvány az alap-betű; a MÁSODIK/HARMADIK sor `tone="tertiary"` (kisebb/halványabb) —
      // ugyanaz a hierarchia, mint a számlázási profilok listáján (rendszer-konvenció).
      className: 'text-muted-foreground',
      multiline: true,
      cell: (inv) => {
        const issued = formatDay(inv.issueDate, locale);
        const due = formatDay(inv.dueDate, locale);
        // A FIZETÉS dátuma is ide tartozik (a legacy dátum-csoportja három dátumot tartalmazott: kiállítás /
        // határidő / fizetés). Csak KIFIZETETT bizonylaton van értéke — a Számlázz.hu státusz-szinkron írja.
        const paid = formatDay(inv.paymentDate, locale);
        if (!issued && !due && !paid) return emDash;
        return (
          <div className="min-w-0 space-y-0.5">
            {issued && (
              <div className="truncate">
                {t('invoices.issueDateLabel')}: {issued}
              </div>
            )}
            {due &&
              (inv.status === 'overdue' ? (
                // LEJÁRT határidő: a danger-szín SZÁNDÉKOSAN erősebb a tertiary-nál — ez figyelmeztetés,
                // nem másodlagos adat (a lista-konvenció alól a státusz-jelzés a kivétel).
                <div className="text-danger truncate">
                  {t('invoices.dueDateLabel')}: {due}
                </div>
              ) : (
                <Text tone="tertiary" as="div" className="truncate">
                  {t('invoices.dueDateLabel')}: {due}
                </Text>
              ))}
            {paid && (
              <div className="text-brand truncate">
                {t('invoices.paymentDateLabel')}: {paid}
              </div>
            )}
          </div>
        );
      },
    },
    {
      // 6. Státusz — a számla állapota (utolsó, dedikált oszlop; a lista-konvenció szerint a státusz mindig utolsó).
      //    A track ALSÓ KORLÁTJA fix (9rem): a badge `whitespace-nowrap`, és 1fr-en 1440 px-nél 92 px-re szűkült
      //    → a leghosszabb felirat („érvénytelenítve" + ikon) 27 px-t lógott ki, a cella vágta (mérve).
      //    A fix minimum tartalom-független track marad (a rács-szabály), csak a padlót emeli.
      id: 'status',
      header: t('invoices.columns.status'),
      cardLabel: t('invoices.columns.status'),
      width: 'minmax(9rem,1fr)',
      cell: (inv) => (
        <InvoiceStatusBadge
          status={inv.status}
          label={t(`invoices.statusLabel.${inv.status}`, { defaultValue: inv.status })}
        />
      ),
    },
  ];

  return (
    <AppShell
      secondaryActions={
        // MOBIL: a sáv két admin-vezérlője (havi generálás · Számlázz.hu státusz-szinkron) rejtve van a
        // szűk sávban — a FAB-ban elérhető marad. Jog nélkül egyik sem kerül be.
        canManage
          ? [
              {
                key: 'generate',
                icon: <DocumentPlusIcon />,
                label: t('invoices.generate.action'),
                onClick: () => setGenerateOpen(true),
              },
              {
                key: 'sync',
                icon: <ArrowPathIcon />,
                label: t('invoices.actions.sync'),
                onClick: () => actions.sync.mutate(),
              },
            ]
          : []
      }
      // Mobilon a lebegő FAB kezeli a létrehozást (csak manage-jog mellett); desktopon a toolbar-gomb (lásd lent).
      // CSAK A „Számlák" FÜLÖN: a kihagyás-napló csak-olvasható diagnosztikai lista, ott a „+ Új számla" a fül
      // EGYETLEN akció-gombja lenne 375 px-en — félrevezető a kontextusban.
      primaryAction={
        canManage && tab === 'invoices'
          ? {
              icon: <PlusIcon className="size-5 stroke-2" />,
              label: t('invoices.new'),
              onClick: () => void navigate({ to: '/invoices/new' }),
            }
          : undefined
      }
      toolbar={
        // FÜL-SÁV (a design.md CC7 lista-konvenciója: `TabStrip`/`Tabs` + KÖZÖS `ListToolbar` + csupasz
        // `DataTable`, a fül a saját sorában). A „Generálási kihagyások" fülnek SAJÁT szűrő-készlete van
        // (hónap/ok/ügyfél/állapot), ezért a KÖZÖS `ListToolbar` csak a „Számlák" fülön jelenik meg — a
        // kihagyás-napló a saját toolbarját hozza (`GenerationSkipsTable`, a lap-törzsben).
        <div className="flex flex-col">
          <div className="flex flex-wrap items-center gap-3 px-4 pt-2">
            <Tabs value={tab} onValueChange={(v) => setFilters({ tab: v })}>
              <TabsList>
                {/* IKONOS fülek, mint minden más füles lapon (jegyek · pénzügy · szolgáltatások): a jel a
                    fül jelentését viszi, és a sáv nélküle idegen volt a többi laphoz képest. */}
                <TabsTrigger value="invoices">
                  <ReceiptPercentIcon />
                  {t('invoices.tabs.invoices')}
                </TabsTrigger>
                <TabsTrigger value="skips">
                  <ExclamationTriangleIcon />
                  {t('invoices.tabs.skips')}
                  {/* A darabszám-plakett CSAK ott, ahol van mit mutatni (a jegy-lista füleinek mintája).
                      A KÖZÖS `TabsCount`: kézzel rajzolva a plakett 4 px-szel magasabbra nyújtotta a fület,
                      és az aktív fül zöld aláhúzása a szürke alapvonal fölé csúszott. */}
                  {skipsCount !== null && skipsCount > 0 && <TabsCount>{skipsCount}</TabsCount>}
                </TabsTrigger>
              </TabsList>
            </Tabs>
          </div>
          {tab === 'invoices' && (
            <ListToolbar
              search={{
                value: searchInput,
                onChange: setSearchInput,
                placeholder: t('common.searchPlaceholder'),
              }}
              // A HÓNAP a sáv ELEJÉN, mindig látható vezérlőként (nem a popoverben) — a Pénzügy- és a
              // használati adatok lap mintája: ez a lap fő dimenziója, amit a felhasználó léptetget.
              // Aktív szűrőnek nem számít (mindig van értéke).
              leading={
                <MonthStepper month={month} onMonthChange={(m) => setFilters({ month: m })} />
              }
              // SORREND-TARTÓ szűrő-lista (user-döntés): TÍPUS (számla/díjbekérő) → kibocsátó →
              // HATÁRIDŐ-tól/-ig → STÁTUSZ (utolsó, a rendszer-konvenció szerint). A KIÁLLÍTÁS-tól/-ig
              // kikerült: ugyanazt a mezőt (`issueDate`) a hónap-léptető vezérli, két vezérlő egy mezőre
              // pedig egymást oltja ki.
              filterItems={[
                {
                  kind: 'select',
                  key: 'type',
                  label: t('invoices.filterType'),
                  value: type,
                  onChange: (v: string) => setFilters({ type: v }),
                  options: INVOICE_DOC_KINDS.map((o) => ({
                    value: o,
                    label: t(`invoices.docKind.${o}`),
                  })),
                  allValue: ALL,
                },
                {
                  kind: 'select',
                  key: 'issuer',
                  label: t('invoices.filterIssuer'),
                  value: issuer,
                  onChange: setIssuer,
                  options: ISSUER_OPTIONS.map((o) => ({
                    value: o,
                    label: t(`invoices.issuerLabel.${o}`, { defaultValue: o }),
                  })),
                  allValue: ALL,
                  // A számlatömb-kódok (MOR/MDIG) NAGYBETŰS rövidítések → nem kisbetűsítjük.
                  preserveCase: true,
                },
                {
                  kind: 'date',
                  key: 'dueFrom',
                  label: t('invoices.filterDueFrom'),
                  value: dueFrom,
                  onChange: (v: string) => setFilters({ dueFrom: v }),
                },
                {
                  kind: 'date',
                  key: 'dueTo',
                  label: t('invoices.filterDueTo'),
                  value: dueTo,
                  onChange: (v: string) => setFilters({ dueTo: v }),
                },
                {
                  kind: 'select',
                  key: 'status',
                  label: t('invoices.filterStatus'),
                  value: status,
                  onChange: (v: string) => setFilters({ status: v }),
                  options: STATUS_OPTIONS.map((s2) => ({
                    value: s2,
                    label: t(`invoices.statusLabel.${s2}`, { defaultValue: s2 }),
                  })),
                  allValue: ALL,
                },
              ]}
              meta={
                infinite.isFetching && !infinite.isFetchingNextPage
                  ? t('invoices.loading')
                  : !isError && !infinite.isLoading
                    ? t('invoices.countOf', { shown: rows.length, total }) +
                      (truncated ? ` · ${t('common.listTruncated')}` : '')
                    : ''
              }
              actions={
                // A generálás és a státusz-szinkron MOBILON a FAB-ban van (`hidden md:flex`) — a szűk sávban
                // a kereső + „Szűrők" az elsődleges. A TÖMEGES KIÁLLÍTÁS viszont minden méreten kint marad:
                // az a KIJELÖLÉSRE jelenik meg, tehát nem ül állandóan a sávban, és a kijelölés maga is
                // szándékos művelet volt (ugyanaz a kivétel, mint a kiadás-lap „Besorolás (N)" gombja).
                //
                // A DOBOZ MOBILON `display: contents` (mért hiba, 375 px): flexként üresen is elvitte a sáv
                // térközét, tehát a kereső + „Szűrők" sor a jobb szél ELŐTT ért véget.
                <div className="contents md:flex md:items-center md:gap-2">
                  {/* Számlagenerálás (admin) — a havi generátor manuális triggere; CSAK manage-jog mellett. */}
                  {canManage && (
                    <Button
                      variant="outline"
                      className="hidden md:inline-flex"
                      onClick={() => setGenerateOpen(true)}
                    >
                      <DocumentPlusIcon className="h-4 w-4" />
                      <span className="hidden sm:inline">{t('invoices.generate.action')}</span>
                    </Button>
                  )}
                  {/* Státusz-szinkron (Számlázz.hu) — CSAK manage-jog mellett; a BFF cache-lock véd a párhuzamtól. */}
                  {canManage && (
                    <Button
                      variant="outline"
                      className="hidden md:inline-flex"
                      onClick={() => actions.sync.mutate()}
                      disabled={actions.sync.isPending}
                    >
                      <ArrowPathIcon
                        className={actions.sync.isPending ? 'h-4 w-4 animate-spin' : 'h-4 w-4'}
                      />
                      <span className="hidden sm:inline">{t('invoices.actions.sync')}</span>
                    </Button>
                  )}
                  {/* TÖMEGES KIÁLLÍTÁS (a legacy „bulk issue") — CSAK akkor látszik, ha van kijelölt DRAFT sor.
                  484 draft számlánál az egyenkénti kiállítás nem üzemeltethető, ezért a legacy is tudta. */}
                  {canManage && selectedDraftIds.length > 0 && (
                    <Button
                      variant="brand"
                      onClick={() => setBulkConfirm(true)}
                      disabled={lifecycle.bulkIssue.isPending}
                    >
                      <PaperAirplaneIcon className="h-4 w-4" />
                      {t('invoices.actions.bulkIssue', { count: selectedDraftIds.length })}
                    </Button>
                  )}
                  {/* A New gomb CSAK ≥md — mobilon a lebegő FAB (primaryAction) kezeli a létrehozást. */}
                  <div className="hidden md:block">
                    <ListNewButton
                      label={t('invoices.new')}
                      onClick={() => void navigate({ to: '/invoices/new' })}
                      enabled={canManage}
                      disabledTooltip={t('common.noCreatePermission')}
                    />
                  </div>
                </div>
              }
            />
          )}
        </div>
      }
    >
      {tab === 'skips' ? (
        <GenerationSkipsTable month={month} onMonthChange={(m) => setFilters({ month: m })} />
      ) : (
        <div className="flex h-full flex-col px-4 pb-2">
          <DataTable<InvoiceRow>
            columns={columns}
            rows={rows}
            getRowId={(inv) => inv.id}
            sort={sort}
            order={order}
            onSort={toggleSort}
            // KIJELÖLÉS: csak manage-joggal és csak DRAFT sorra (a nem-draft kiállítása 409 lenne).
            selection={
              canManage
                ? {
                    selectedIds,
                    onToggle: (id) =>
                      setSelectedIds((prev) => {
                        const next = new Set(prev);
                        if (next.has(id)) next.delete(id);
                        else next.add(id);
                        return next;
                      }),
                    // A „mind" CSAK a betöltött, DRAFT sorokra hat (a többi kiállítása 409 lenne) — a
                    // szűrést az `isSelectable` végzi, tehát az `ids` MÁR csak piszkozatokat tartalmaz.
                    onToggleAll: (ids, on) => setSelectedIds(on ? new Set(ids) : new Set()),
                    isSelectable: (r) => r.status === 'draft',
                    rowLabel: t('invoices.selectRow'),
                    allLabel: t('invoices.selectAll'),
                  }
                : undefined
            }
            isLoading={!gated && (permsLoading || infinite.isLoading)}
            isError={isError}
            errorState={
              <ListErrorState forbidden={isForbidden} onRetry={() => void infinite.refetch()} />
            }
            emptyState={
              hasFilters ? (
                <EmptyState
                  title={t('invoices.noMatches')}
                  description={t('invoices.noMatchesDesc')}
                />
              ) : (
                <EmptyState title={t('invoices.empty')} description={t('invoices.emptyDesc')} />
              )
            }
            hasNextPage={infinite.hasNextPage}
            isFetchingNextPage={infinite.isFetchingNextPage}
            onLoadMore={loadMore}
            // Sor-kattintás → read-only számla-detail (minden státuszon; a tickets-lista mintája).
            onRowClick={(inv) => void navigate({ to: '/invoices/$id', params: { id: inv.id } })}
            rowActionsLabel={t('invoices.columns.actions')}
            // Sor-akciók: MINDEN soron „Megtekintés" (szem); draft soron + manage → ceruza, + delete → kuka.
            // Az oszlop-szélesség a LEGTÖBB egyszerre látható ikonhoz igazodik: manage mellett 4 (draft: szem +
            // ceruza + kiállítás + kuka · kiállított díjbekérő: szem + PDF + sztornó + érvénytelenítés) →
            // 4×30 px + 3×4 px köz + 2×14 px cella-belső = 160 px ≈ 10 rem; anélkül 2 (szem + PDF) → 6 rem.
            // A korábbi 15 rem a státusz-oszloptól vett el helyet (a badge vágódott — mérve).
            actionsColWidth={canManage ? '10rem' : '6rem'}
            rowActions={(inv) => (
              <InvoiceRowActions
                row={inv}
                canManage={canManage}
                canDelete={canDelete}
                onView={(row) => void navigate({ to: '/invoices/$id', params: { id: row.id } })}
                onEdit={(row) =>
                  void navigate({ to: '/invoices/$id/edit', params: { id: row.id } })
                }
                onDelete={(row) => setDeleteTarget(row)}
                onIssue={(row) => actions.openIssue(row)}
                onStorno={(row) => actions.openStorno(row)}
                onCancelProforma={(row) => actions.openCancel(row)}
                onPdf={(row) =>
                  void actions.downloadPdf(row.id, row.status === 'storno' ? 'storno' : 'invoice')
                }
                pdfPending={actions.pdfPendingId === inv.id}
                t={t}
              />
            )}
          />
        </div>
      )}

      <ConfirmDialog
        open={deleteTarget !== null}
        onOpenChange={(open) => !open && setDeleteTarget(null)}
        title={t('invoices.deleteTitle')}
        description={t('invoices.deleteDesc')}
        confirmLabel={t('invoices.delete')}
        cancelLabel={t('invoices.cancel')}
        pendingLabel={t('invoices.deleting')}
        destructive
        pending={removeMutation.isPending}
        onConfirm={confirmDelete}
      />

      {/* TÖMEGES kiállítás megerősítése — valódi, VISSZAFORDÍTHATATLAN kimenő művelet (a Számlázz.hu
          sorszámot ad ki), ezért megerősítést kér, és kiírja a darabszámot. */}
      <ConfirmDialog
        open={bulkConfirm}
        onOpenChange={(open) => !open && setBulkConfirm(false)}
        title={t('invoices.actions.bulkIssueTitle')}
        description={t('invoices.actions.bulkIssueDesc', { count: selectedDraftIds.length })}
        confirmLabel={t('invoices.actions.bulkIssueConfirm')}
        cancelLabel={t('invoices.cancel')}
        pending={lifecycle.bulkIssue.isPending}
        onConfirm={() =>
          lifecycle.bulkIssue.mutate(
            { ids: selectedDraftIds },
            { onSettled: () => setBulkConfirm(false) },
          )
        }
      />

      {/* Kiállítási életciklus dialógusok (kiállítás / sztornó / díjbekérő-érvénytelenítés). */}
      <InvoiceActionDialogs controller={actions} t={t} />

      {/* Számlagenerálás-dialógus (admin, invoices.manage) — a MEGLÉVŐ POST /invoices/generate trigger. */}
      {canManage && (
        <InvoiceGenerateDialog
          open={generateOpen}
          onOpenChange={setGenerateOpen}
          onViewSkips={(m) => setFilters({ tab: 'skips', month: m })}
        />
      )}
    </AppShell>
  );
}
